// NotificationItem.js - Individual Notification Component
import React, { useState } from "react";
import { Trash2, ExternalLink, Clock } from "lucide-react";
import { API_METHODS } from "../../../constants";
import useApi from "../../../hooks/useApi";
import { useNavigate } from "react-router-dom";

const NotificationItem = ({ notification, onUpdate, onClose }) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  // API hooks
  const [markReadResponse, markAsRead] = useApi();
  const [deleteResponse, deleteNotification] = useApi();

  const handleMarkAsRead = (e) => {
    e.stopPropagation();
    if (!notification.is_read) {
      markAsRead(
        {},
        API_METHODS.PUT,
        `/user/notifications/read/${notification.id}`
      );
      onUpdate && onUpdate();
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this notification?")) {
      deleteNotification(
        {},
        API_METHODS.DELETE,
        `/user/notifications/${notification.id}`
      );
    }
  };

  // Handle delete response
  React.useEffect(() => {
    if (deleteResponse.data && !deleteResponse.isLoading) {
      onUpdate && onUpdate(); // Refresh the list after successful deletion
    }
  }, [deleteResponse, onUpdate]);

  const handleClick = () => {
    // Mark as read when clicked
    if (!notification.is_read) {
      handleMarkAsRead({ stopPropagation: () => {} });
    }

    // Navigate to action URL if available
    if (notification.action_url) {
      onClose && onClose();
      navigate(notification.action_url);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "text-red-600 bg-red-50 border-red-200";
      case "medium":
        return "text-orange-600 bg-orange-50 border-orange-200";
      case "low":
        return "text-green-600 bg-green-50 border-green-200";
      default:
        return "text-blue-600 bg-blue-50 border-blue-200";
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "order":
        return "📋";
      case "payment":
        return "💰";
      case "system":
        return "🔔";
      case "support":
        return "💬";
      default:
        return "ℹ️";
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const notificationDate = new Date(dateString);
    const diffInMinutes = Math.floor((now - notificationDate) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return notificationDate.toLocaleDateString("en-IN");
  };

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        p-4 cursor-pointer transition-all duration-200 relative
        ${
          !notification.is_read
            ? "bg-gradient-to-r from-orange-25 to-red-25 border-l-4 border-orange-400"
            : "hover:bg-gray-50"
        }
        ${notification.action_url ? "hover:bg-orange-25" : ""}
      `}
    >
      {/* Unread indicator */}
      {!notification.is_read && (
        <div className="absolute top-4 right-4">
          <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
        </div>
      )}

      {/* Notification Content */}
      <div className="flex items-start space-x-3">
        {/* Type Icon */}
        <div className="flex-shrink-0 mt-1">
          <div
            className={`
            w-8 h-8 rounded-full flex items-center justify-center text-sm
            ${getPriorityColor(notification.priority)}
            border
          `}
          >
            {getTypeIcon(notification.type)}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4
                className={`
                text-sm font-medium mb-1 line-clamp-1
                ${!notification.is_read ? "text-gray-900" : "text-gray-700"}
              `}
              >
                {notification.title}
              </h4>

              <p
                className={`
                text-sm line-clamp-2 mb-2
                ${!notification.is_read ? "text-gray-700" : "text-gray-600"}
              `}
              >
                {notification.message}
              </p>

              <div className="flex items-center space-x-4 text-xs text-gray-500">
                <div className="flex items-center space-x-1">
                  <Clock className="h-3 w-3" />
                  <span>{formatTimeAgo(notification.created_on)}</span>
                </div>

                {notification.priority !== "low" && (
                  <span
                    className={`
                    px-2 py-1 rounded-full text-xs font-medium
                    ${getPriorityColor(notification.priority)}
                  `}
                  >
                    {notification.priority}
                  </span>
                )}

                {notification.action_url && (
                  <div className="flex items-center space-x-1 text-orange-600">
                    <ExternalLink className="h-3 w-3" />
                    <span>View details</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons (shown on hover) */}
      {isHovered && (
        <div className="absolute top-2 right-8 flex items-center space-x-1">
          {!notification.is_read && (
            <button
              onClick={handleMarkAsRead}
              disabled={markReadResponse.isLoading}
              className="p-1 text-green-600 hover:bg-green-100 rounded-lg transition-all"
              title="Mark as read"
            >
              <div className="w-4 h-4 border-2 border-green-600 rounded-full bg-green-600"></div>
            </button>
          )}

          <button
            onClick={handleDelete}
            disabled={deleteResponse.isLoading}
            className="p-1 text-red-500 hover:bg-red-100 rounded-lg transition-all"
            title="Delete notification"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Loading overlay */}
      {(markReadResponse.isLoading || deleteResponse.isLoading) && (
        <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
};

export default NotificationItem;
